"use client";

import { useSearchParamsQuery } from "@finbackoffice/site-core";
import { IEventFeedResponse } from "@finbackoffice/websocket-client";
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    useMemo,
    useState,
} from "react";
import { EventStatus } from "@finbackoffice/enums";
import { useParams } from "next/navigation";

export type IActiveSportGameState = {
    gameId: string | null;
    tournament: { id: number; gamesSnapshot: IEventFeedResponse[] } | null;
};

export interface IActiveSportGameContext {
    activeGameState: IActiveSportGameState;
    setActiveGameState: Dispatch<SetStateAction<IActiveSportGameState>>;
    currentGameStatus: EventStatus.Prematch | EventStatus.Live | null;
    setCurrentGameStatus: (val: EventStatus.Live | EventStatus.Prematch | null) => void;
}

export const ActiveSportGameContext = createContext<IActiveSportGameContext>(null as any);

export const ActiveSportGameProvider: FC<PropsWithChildren> = ({ children }) => {
    const params = useParams();
    const { query } = useSearchParamsQuery();

    const defaultActiveSportGame = useMemo(() => {
        if (!params?.gameId && query?.gameId) {
            return query?.gameId as string;
        }
        return null;
    }, [params?.gameId, query?.gameId]);

    const [activeGameState, setActiveGameState] = useState<IActiveSportGameState>({
        gameId: defaultActiveSportGame,
        tournament: null,
    });

    const [currentGameStatus, setCurrentGameStatus] = useState<
        EventStatus.Live | EventStatus.Prematch | null
    >(null);

    const value = useMemo(
        () => ({
            activeGameState,
            setActiveGameState,
            currentGameStatus,
            setCurrentGameStatus,
        }),
        [activeGameState, currentGameStatus],
    );

    return (
        <ActiveSportGameContext.Provider value={value}>{children}</ActiveSportGameContext.Provider>
    );
};
